import React, { memo, ReactElement, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { checkOperationDelete } from '../../utils/OperationPermissions';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { areEqualProps } from './utils/shallowCompare';
import { IFormAction } from '../../@types/entityTypes';
import { usePerformSave } from './Hooks/usePerformSave';
import { BasicConfirmation } from '../modals/ConfirmationPrompt';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { AlertType } from '../../graphql/schema/p4types';

export const useDeleteRecord = ({
  archive,
  archiveWarningSuffix,
}: {
  archive?: boolean;
  archiveWarningSuffix?: string;
}): [deleteAction: () => void, deleteModals: ReactElement] => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { values, entity } = useContext(ConnectedFormContext);
  const { dispatchSimpleToast } = useP4ToastController();

  const action = archive ? 'archive' : 'delete';
  const fieldToChange = useMemo(() => (action === 'delete' ? 'deleted' : 'archived'), [action]);
  const capitalizedAction = useMemo(() => `${action?.[0]?.toUpperCase() ?? ''}${action?.slice(1) ?? ''}`, [action]);
  const formTitle = entity?.formTitle;

  const [performSave, performSaveModals] = usePerformSave(
    () => {
      dispatchSimpleToast(`${capitalizedAction} successful`, `${formTitle} ${fieldToChange}`, AlertType.Success);
    },
    () => {
      dispatchSimpleToast(`${capitalizedAction} failed`, `${formTitle} was not ${fieldToChange}`, AlertType.Error);
    }
  );

  const handleDeleteClick = useCallback(() => {
    setConfirmationOpen(true);
  }, []);

  const onConfirmDelete = useCallback(() => {
    performSave(undefined, { ...values, [fieldToChange]: true });
    setConfirmationOpen(false);
  }, [performSave, fieldToChange, values]);

  const onCancelDelete = useCallback(() => {
    setConfirmationOpen(false);
  }, []);

  return [
    handleDeleteClick,
    <>
      <BasicConfirmation
        isOpen={confirmationOpen}
        title={'Confirm Delete'}
        message={`Do you want to ${action} this record? ${archiveWarningSuffix ?? ''}`}
        onConfirm={onConfirmDelete}
        onCancel={onCancelDelete}
      />
      {performSaveModals}
    </>,
  ];
};

export const useRestoreRecord = (): [restoreAction: () => void, restoreModals: ReactElement] => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const { values, entity } = useContext(ConnectedFormContext);
  const { dispatchSimpleToast } = useP4ToastController();

  const action = 'restore';
  const fieldToChange = 'restored';
  const capitalizedAction = useMemo(() => `${action?.[0]?.toUpperCase() ?? ''}${action?.slice(1) ?? ''}`, [action]);
  const formTitle = entity?.formTitle;

  const [performSave, performSaveModals] = usePerformSave(
    () => {
      dispatchSimpleToast(
        `${capitalizedAction} successful`,
        `${formTitle} ${fieldToChange} successfully`,
        AlertType.Success
      );
    },
    () => {
      dispatchSimpleToast(`${capitalizedAction} failed`, `${formTitle} was not ${fieldToChange}`, AlertType.Error);
    }
  );

  const handleRestoreClick = useCallback(() => {
    setConfirmationOpen(true);
  }, []);

  const onConfirmRestore = useCallback(() => {
    performSave(undefined, { ...values, [fieldToChange]: true });
    setConfirmationOpen(false);
  }, [performSave, fieldToChange, values]);

  const onCancelDelete = useCallback(() => {
    setConfirmationOpen(false);
  }, []);

  return [
    handleRestoreClick,
    <>
      <BasicConfirmation
        isOpen={confirmationOpen}
        title={'Confirm Restore'}
        message={`Do you want to ${action} this record?`}
        onConfirm={onConfirmRestore}
        onCancel={onCancelDelete}
      />
      {performSaveModals}
    </>,
  ];
};

const ConnectedHeader = memo((props: { actionButtons: Array<IFormAction<any>> } & any) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { cancel, submit, readOnly, getFormState } = connectedForm;
  const { archive, entityHdr, entityHdr2, status, detailView, archiveWarningSuffix } = props;

  const headerProps = useSelector((store: any) => {
    const formState = getFormState?.(store);
    const values = formState?.values;
    const app = store.app;
    //checkOperationDelete expects form props... But we need to pass updated valued also for checking status flags.
    const deletePermitted = checkOperationDelete({ ...connectedForm, values, app });

    return {
      entityHdr,
      entityHdr2,
      values,
      status,
      readOnly,
      deletePermitted,
      detailView,
    };
  });

  const [handleDelete, deleteModals] = useDeleteRecord({
    archive: archive,
    archiveWarningSuffix: archiveWarningSuffix,
  });

  const [handleRestore, restoreModals] = useRestoreRecord();

  const { component: HeaderComponent, onCancel, onSubmit, ...rest } = props;

  return (
    <>
      <HeaderComponent
        onCancel={onCancel || cancel}
        onDelete={handleDelete}
        onRestore={handleRestore}
        onSubmit={onSubmit || submit}
        name={props.name || connectedForm.name}
        {...rest}
        {...headerProps}
      />
      {deleteModals}
      {restoreModals}
    </>
  );
}, areEqualProps);

export default ConnectedHeader;
